/**
 * Místní kopie appky v IndexedDB místo `localStorage` — konec „plného prohlížeče".
 *
 * Tom, 2. 11. 2026: *„No tak to ale musíme udělat, když to hlásí, že máme plný prohlížeč… Je lepší si počkat
 * na relevantní data, než aby nám spadl prohlížeč."* `localStorage` má ~5 MB na celou appku a my jsme ho
 * přerostli (4,6 MB u všech profilů). Data se dál posílají na server přesně jako dosud — tohle mění jen to,
 * KDE si prohlížeč drží svou kopii.
 *
 * Jak:
 *  1. Před startem appky (`main.tsx` čeká) se načte celá kopie z IndexedDB do paměti (Map).
 *  2. Co je ještě ve starém `localStorage`, se přenese (jednorázově) a staré úložiště se vyčistí.
 *  3. `localStorage.getItem/setItem/removeItem/key/length/clear` se přesměrují na tu Map — synchronně,
 *     takže zustand `persist` i všechny synchronizace se serverem fungují beze změny. Každý zápis
 *     se zároveň uloží do IndexedDB (limit stovky MB, ne 5 MB).
 *
 * Když IndexedDB není (soukromé okno, starý prohlížeč), appka jede postaru z `localStorage`.
 * `sessionStorage` se nemění.
 */
const DB_NAZEV = 'rw-uloziste';
const STORE = 'kv';
/** Klíč v IndexedDB: přenos ze starého localStorage už proběhl (v appce není vidět). */
const ZNACKA_PRENOSU = '__rw_prenos_z_localstorage';

let mapa: Map<string, string> | null = null;
let db: IDBDatabase | null = null;

function otevri(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAZEV, 1);
    r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE); };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
    r.onblocked = () => rej(new Error('IndexedDB blokovaná'));
  });
}

function nactiVse(d: IDBDatabase): Promise<Map<string, string>> {
  return new Promise((res, rej) => {
    const out = new Map<string, string>();
    const tx = d.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (c) { if (typeof c.value === 'string') out.set(String(c.key), c.value); c.continue(); }
    };
    tx.oncomplete = () => res(out);
    tx.onerror = () => rej(tx.error);
  });
}

function zapisVse(d: IDBDatabase, polozky: [string, string][]): Promise<void> {
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, 'readwrite');
    const st = tx.objectStore(STORE);
    for (const [k, v] of polozky) st.put(v, k);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
    tx.onabort = () => rej(tx.error);
  });
}

function uloz(klic: string, hodnota: string | null): void {
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    if (hodnota === null) tx.objectStore(STORE).delete(klic);
    else tx.objectStore(STORE).put(hodnota, klic);
    tx.onerror = () => console.warn('[uloziste-idb] zápis selhal:', klic, tx.error);
  } catch (e) { console.warn('[uloziste-idb] zápis selhal:', klic, e); }
}

function smazVse(): void {
  if (!db) return;
  try { db.transaction(STORE, 'readwrite').objectStore(STORE).clear(); } catch { /* nic */ }
}

/** Jen pro diagnostiku: kolik toho kopie drží (znaky × 2 ≈ bajty). */
export function velikostKopie(): number {
  if (!mapa) return 0;
  let b = 0;
  for (const [k, v] of mapa) b += (k.length + v.length) * 2;
  return b;
}

/**
 * Připraví úložiště. `main.tsx` na tohle čeká, než natáhne appku — ať při startu nikdo nečte prázdno.
 * Trvá desítky milisekund; nikdy nehodí (při chybě zůstane starý `localStorage`).
 */
export async function pripravUloziste(): Promise<void> {
  try {
    if (typeof indexedDB === 'undefined' || typeof window === 'undefined' || !window.localStorage) return;
    const ls = window.localStorage;
    const proto = Storage.prototype;
    const puv = {
      getItem: proto.getItem, setItem: proto.setItem, removeItem: proto.removeItem,
      key: proto.key, clear: proto.clear,
      length: Object.getOwnPropertyDescriptor(proto, 'length')?.get,
    };
    db = await otevri();
    const vse = await nactiVse(db);

    /* JEDNORÁZOVÝ přenos ze starého localStorage (značka v IndexedDB). Staré úložiště se NEMAŽE — zůstane
       jako záloha pro případ, že by IndexedDB někdy nešla; zapisovat se do něj už nebude, takže limit nevadí. */
    const prenest: [string, string][] = [];
    if (!vse.has(ZNACKA_PRENOSU)) {
      const delka = puv.length ? (puv.length.call(ls) as number) : 0;
      for (let i = 0; i < delka; i += 1) {
        const k = puv.key.call(ls, i);
        if (k == null || vse.has(k)) continue;
        const v = puv.getItem.call(ls, k);
        if (v != null) { vse.set(k, v); prenest.push([k, v]); }
      }
      await zapisVse(db, [...prenest, [ZNACKA_PRENOSU, new Date().toISOString()]]);
    }
    vse.delete(ZNACKA_PRENOSU);

    mapa = vse;
    const m = vse;
    proto.getItem = function getItem(this: Storage, k: string) {
      return this === ls ? (m.has(String(k)) ? m.get(String(k))! : null) : puv.getItem.call(this, k);
    };
    proto.setItem = function setItem(this: Storage, k: string, v: string) {
      if (this !== ls) return puv.setItem.call(this, k, v);
      const kk = String(k); const vv = String(v);
      m.set(kk, vv); uloz(kk, vv);
    };
    proto.removeItem = function removeItem(this: Storage, k: string) {
      if (this !== ls) return puv.removeItem.call(this, k);
      m.delete(String(k)); uloz(String(k), null);
    };
    proto.key = function key(this: Storage, i: number) {
      if (this !== ls) return puv.key.call(this, i);
      return [...m.keys()][i] ?? null;
    };
    proto.clear = function clear(this: Storage) {
      if (this !== ls) return puv.clear.call(this);
      m.clear(); smazVse();
    };
    if (puv.length) {
      const puvLength = puv.length;
      Object.defineProperty(proto, 'length', { configurable: true, get(this: Storage) { return this === ls ? m.size : puvLength.call(this); } });
    }
    console.info('[uloziste-idb] kopie appky v IndexedDB:', m.size, 'klíčů,', Math.round(velikostKopie() / 1024), 'kB',
      prenest.length ? `· přeneseno z localStorage: ${prenest.length}` : '');
  } catch (e) {
    console.warn('[uloziste-idb] IndexedDB nejde — appka jede z localStorage:', e);
    mapa = null;
  }
}
